/* #region(collapsed) DEVELOPER DOCUMENTATION ═══════════════════════════════════════════════ */
/**
  @license
  # COPYRIGHT NOTICE
    © CROUIN 2020-2025. All rights reserved. Powered by MaxDesign.
    ⚠ FR : CONTENU PROTÉGÉ PAR LES DROITS D'AUTEURS (article L. 111-1 du code de la propriété intellectuelle). Cette ressource ne peut pas être utilisée, diffusée ou exploitée sans l'approbation de ses auteurs. Pour plus d'informations, veuillez contacter admin(at)maxdesign.expert
    ⚠ EN : CONTENT PROTECTED WITH AUTHOR COPYRIGHTS (section L. 111-1 of the intellectual property code). This resource may not be used, distributed or exploited without the approval of its authors. For any information request, please contact admin(at)maxdesign.expert
 
  # INTRODUCTION
    2-in-1 module (cart page / cart modal) with a shared set of settings.
    The cart features mostly rely on HTML custom elements: modal, cart-controller, cart-ui and cart-item.
    All methods can be accessed with the javascript Class MaxDesign (see "popular javascript methods" bellow).
 
  # POPULAR JAVASCRIPT METHODS
    const controller = MaxDesign.cart.controller; // get cart controller
    const cart = MaxDesign.cart.UI; // get cart UI
    const cartJson = controller.settings.cart; // get cart json data
    controller.editCart('add|change|update', { data, btn, form, scroll, load }); // modify the cart (pass form HTMLElement, or data object)
    controller.render(await controller.fetch()); // render updated cart (async function)
    controller.modal.open(); // open cart modal
    document.addEventListener('maxdesign:cart:add|update|change|render', console.log); // new item added/changed/.../ to cart with request response
 
  # MORE INFORMATION
    - Contact us at admin(at)maxdesign.expert
    - Visit shopify cart API https://shopify.dev/api/ajax/reference/cart
*/
/* #endregion ═══════════════════════════════════════════════════════════════════════════════ */

window.MaxDesign = window.MaxDesign || class {};
MaxDesign.cart = {
  get controller() {
    return document.getElementById('cmd');
  },
  get UI() {
    return document.getElementById('cmd-ui');
  },
  recommendationsCache: {},
};

/* #region(collapsed) CART CONTROLLER ═══════════════════════════════════════════════════════ */
class CartController extends HTMLElement {
  async connectedCallback() {
    this.aborter = new AbortController();
    this.settings = this.json(this);
    this.sectionId = document.querySelector('.cart-maxdesign').id.slice(16);
    this.modal = MaxDesign?.modal?.cart;
    await Promise.all(['cart-ui-maxdesign', 'cart-item-maxdesign'].map(el => customElements.whenDefined(el)));
    document.addEventListener('click', this.onFormSubmit.bind(this), {
      capture: this.settings.apps.product_atc_override == 'force_2',
      signal: this.aborter.signal,
    });
    document.addEventListener('maxdesign:cart:add', this.onCartAdd.bind(this), { signal: this.aborter.signal });
  }

  disconnectedCallback() {
    this.aborter.abort();
  }

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) On Event */

  /**
   * On form submission with event bubbling for dynamic ATC.
   * Conditional step to check if form is valid
   * In some case we may use a custom Funciton() to support third party apps
   */
  async onFormSubmit(e) {
    const submitButtons = document.querySelectorAll(this.settings.submit.buttons);
    for (let btn of submitButtons) {
      const form = btn.form || btn.closest('form[action*="/cart/add"]');
      if (this.preventFormSubmit(form)) continue;
      if (btn.contains(e.target) && !btn.disabled && form && this.isFormValid(form, e)) {
        if (this.settings.apps.product_atc_override !== 'default') {
          e.preventDefault();
          this.settings.apps.product_atc_override == 'force_2' && e.stopImmediatePropagation();
        }
        let payload = { btn, form, openModal: !!this.openModal(btn) };
        if (this.settings.submit.function) {
          try {
            payload = new Function('payload', this.settings.submit.function)(payload) || payload;
          } catch (error) {
            console.error('Error in custom Function():', error);
          }
        }
        await this.editCart('add', payload);
        break;
      }
    }
  }

  /**
   * Close all modals when cart modal is opening
   */
  onCartAdd() {
    const modals = MaxDesign?.modal?.controller?.modals;
    modals && modals.forEach(modal => modal.hasAttribute('open') && modal.dataset.id !== 'cart' && modal.close());
  }

  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Auxiliary Methods */

  /**
   * Handles common functionality for making requests to the cart
   * Generate an event such as 'cart:add', 'cart:update', etc.
   * @param {string} endpoint - API endpoint for the request
   * @param {Object} formData - FormData object containing the request data
   * @param {Boolean} render - Whether to render cart
   */
  async handleCartRequest(endpoint, formData, render = true) {
    const route = Shopify.routes.root;
    this.formData(formData, 'sections', this.sectionId);
    this.formData(formData, 'sections_url', route + 'cart');
    const response = await fetch(route + endpoint, { method: 'POST', body: formData });
    const result = await response.json();
    if (response.status >= 300) throw result;
    this.event('maxdesign:' + endpoint.replace('/', ':').replace('.js', ''), result);
    render && this.render(result.sections[this.sectionId]);
  }

  /**
   * Add, change or update cart from form HTMLElement or data object
   * @param {string} endpoint - Either add, change or update
   * @param {Object} data - Items data based on endpoint
   * @param {HTMLElement} btn - Button element triggering the request
   * @param {HTMLElement} form - Form element
   * @param {Boolean} scroll - Whether to scroll to the cart modal after the request
   * @param {Boolean} openModal - Whether to open cart modal after the request
   * @param {Boolean} load - Whether to show loading animation
   * @param {Boolean} render - Whether to render cart
   */
  async editCart(
    endpoint,
    { data = {}, btn = undefined, form = undefined, scroll = true, openModal = true, load = true, render = true }
  ) {
    try {
      if (btn) this.loading = { el: btn, load };
      else if (this.modal) this.modal.loading = load;
      else this.loading = { el: this, load };
      let formData = new FormData(form);
      Object.keys(data).forEach(key => this.formData(formData, key, data[key]));
      formData = this.reverseFormDataItems(formData);
      const groupId = formData.get('properties[_id]');
      const discount = formData.get('discount');
      if (groupId) await this.editGroup(groupId);
      if (discount) await this.editDiscounts({ add: discount, render: false });
      await this.handleCartRequest(`cart/${endpoint}.js`, formData, render);
      openModal && this.modal?.open();
    } catch (error) {
      alert(`${error.description}`);
      console.error(error);
      render && this.render(await this.fetch());
      openModal && this.modal?.open();
    } finally {
      if (endpoint == 'add' && this.modal && scroll) this.modal.scroll = {};
      if (btn) this.loading = { el: btn, load: false, info: !openModal && btn ? this.settings.quickBuy?.info : false };
      else if (this.modal) this.modal.loading = false;
      else this.loading = { el: this, load: false };
    }
  }

  /**
   * We replace products with similar group id, which happens when user edit group from cart
   * @param {String} id - group id
   */
  async editGroup(id) {
    if (new URLSearchParams(location.search).has('group')) history.replaceState(null, '', location.pathname);
    const cart = await this.cart();
    const itemsToRemove = cart.items.filter(({ properties }) => properties?._id == id);
    if (itemsToRemove.length) {
      const updates = itemsToRemove.reduce((obj, item) => ({ ...obj, [item.key]: 0 }), {});
      await this.editCart('update', { data: { updates }, scroll: false, openModal: false });
    }
  }

  /**
   * Retrieve active codes and add/remove new code(s), then render cart
   * We handle code errors by comparing old and new cart attribute
   * We place the code in finally because iOS + ShopPay returns a CORS error
   * !Might need to figure out "wallets/checkouts" API (test code bellow)
   * @param {Object} param0 - The parameters for editing discounts
   * @param {string} [param0.add=''] - Codes to be added, separated by commas
   * @param {string} [param0.remove=''] - Code to be removed
   * @param {boolean} [param0.render=true] - Whether to render the result
   * @param {HTMLElement} param0.btn - The button element that triggered the edit
   */
  async editDiscounts({ add = '', remove = '', render = true, btn }) {
    try {
      this.loading = { el: btn, load: true };
      const active = MaxDesign.cart.UI?.discount?.codes?.split(',') || [];
      const discounts = [...new Set([...active, ...add.split(',')].filter(code => code != remove))];
      await fetch(`${Shopify.routes.root}checkout?discount=${discounts.join(',') || 'clear'}`);
    } catch (error) {
      console.error(error);
    } finally {
      if (render) {
        this.render(await this.fetch());
        const UI = MaxDesign.cart.UI;
        const active = UI.discount.codes?.split(',').map(code => code.toLowerCase());
        const codesNotAdded = add
          .split(',')
          .map(code => code.trim())
          .filter(code => !active.includes(code.toLowerCase()) && code !== '');
        if (codesNotAdded.length > 0) {
          const codePlaceholder = UI.DOM.discount.text.querySelector('span[data-code]');
          codePlaceholder && (codePlaceholder.dataset.code = codesNotAdded.join(', '));
          UI.DOM.discount.text.hidden = false;
          UI.DOM.discount.input.style.setProperty('--color-discount-field', 'var(--color-critical-error)');
        }
      } else this.loading = { el: btn, load: false };
    }
  }

  /**
   * fetch cart page using section rendering API
   * @returns {Object} cart markup string
   */
  async fetch() {
    const url = `${Shopify.routes.root}cart?section_id=${this.sectionId}`;
    return fetch(url)
      .then(response => response.text())
      .catch(error => {
        console.error(error);
        location.href = `${Shopify.routes.root}cart`;
      });
  }

  /**
   * Render cart section passed as parameter
   * Replace existing UI or append it in controller otherwise
   * We preserve sticky and timer state to avoid repetitive animation on cart change
   * @param {String} response - cart markup as text
   */
  render(response) {
    const newUI = new DOMParser().parseFromString(response, 'text/html').querySelector('#cmd-ui');
    const currentUI = MaxDesign.cart.UI;
    if (currentUI) {
      // Preserve some attributes and elements to improve transitions
      ['data-sticky', 'data-timer', 'data-addon'].forEach(a => newUI.toggleAttribute(a, currentUI?.hasAttribute(a)));
      ['.cmd-timer', '.cmd-shipping', '.cmd-offer__progress', '[data-recommendations-result]'].forEach(selector => {
        const el = currentUI?.querySelector(selector);
        el && newUI.querySelector(selector)?.replaceWith(el);
      });
      // replace UI
      currentUI.replaceWith(newUI);
    } else this.append(newUI);
    this.event('maxdesign:cart:render', newUI.settings.cart);
  }

  /**
   * We edit formData instance.
   * Empty data should be appendable as long as there's a key.
   * @param {instance} formData - FormData instance
   * @param {string} key
   * @param {string} data
   */
  formData(formData, key, data) {
    if (data === Object(data) || Array.isArray(data)) {
      for (const i in data) this.formData(formData, `${key}[${i}]`, data[i]);
    } else formData.append(key, data);
  }

  /**
   * We reverse the indexed FormData items[], so that they are displayed in expected order in cart
   * @param {FormData} - original FormData
   * @returns FormData
   */
  reverseFormDataItems(formData) {
    let formDataArray = Array.from(formData.entries());
    formDataArray.sort(([a], [b]) => {
      const indexA = parseInt(a.match(/\[\d+\]/)?.[0].slice(1, -1));
      const indexB = parseInt(b.match(/\[\d+\]/)?.[0].slice(1, -1));
      return indexB !== null && indexA !== null ? indexB - indexA : 0;
    });
    let reversedFormData = new FormData();
    formDataArray.forEach(([key, value]) => reversedFormData.append(key, value));
    return reversedFormData;
  }

  /**
   * Check form validation. We display an alert in case the element is not focusable
   * Progressive enhancement to highlight invalid element that is not visible/focusable when submitting form
   * Timeout allows to wait for third-party code to update element visibility/focusability
   * @param {HTMLFormElement} form
   * @param {Object} e - click event
   * @returns Boolean
   */
  isFormValid(form, e) {
    const invalidElement = [...form.elements].find(el => !el.checkValidity());
    if (!invalidElement) return true;
    e.preventDefault();
    e.stopPropagation();
    document.documentElement.style.scrollBehavior = 'auto';
    invalidElement.reportValidity();
    document.documentElement.style.scrollBehavior = '';
    setTimeout(() => {
      if (!invalidElement.matches(':focus')) {
        const alert = `${invalidElement.name?.replace(/properties|\[|\]/g, '')} : ${invalidElement.validationMessage}`;
        const checkVisibilitySupport = typeof invalidElement.checkVisibility !== 'undefined';
        if (checkVisibilitySupport) {
          let parent = invalidElement;
          while (parent) {
            if (parent.checkVisibility()) {
              parent.style.outline = '2px solid red';
              setTimeout(() => {
                window.alert(alert);
                parent.style.outline = '';
              }, 25);
              break;
            }
            parent = parent.parentElement;
          }
        } else alert(alert);
      }
    });
    return false;
  }

  event(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Getters & Setters */

  /**
   * Set loader animation when loading discount, cart or checkout
   * We can also show info in ATC when the cart modal is set to not open for quick buy ATC
   * We wrap text node in span element to hide them when button is loading
   */
  set loading({ el, load, info }) {
    if (!el) return;
    el.childNodes.forEach(
      node =>
        node.nodeType === Node.TEXT_NODE &&
        node.textContent.trim() &&
        node.replaceWith(Object.assign(document.createElement('span'), { textContent: node.textContent }))
    );
    if (info) {
      el.dataset.info = info;
      el.dataset.loader = 'info';
      setTimeout(() => (el.dataset.loader = ''), 2000); /* based on css animation duration */
    } else el.dataset.loader = load ? 'loading' : '';
  }

  /**
   * allows cart to override all listeners, except for bundle and upsell modules
   */
  get preventFormSubmit() {
    return form => {
      const upsell = MaxDesign?.product?.upsell;
      const bundles = [...(MaxDesign?.bundles?.get?.() || [])];
      const isSameUpsellForm = form === upsell?.form;
      const isSameBundleForm = bundles.some(
        bundle =>
          bundle?.atcBtn &&
          [...bundle.atcBtn].some(
            atc => (atc.form === form.getAttribute('id') || atc.closest('form[action*="/cart/add"]')) === form
          )
      );
      return isSameBundleForm || (upsell?.activeUpsells?.length && isSameUpsellForm);
    };
  }

  /**
   * @returns {Object} custom element settings
   */
  get json() {
    return scope => JSON.parse(scope.querySelector(':scope > [type="application/json"]')?.innerText || '{}');
  }

  /**
   * Get cart json data
   * Use before cart is rendered, otherwise access cart.settings.cart object
   * @returns {Object} cart json data
   */
  get cart() {
    return async () => {
      const url = `${Shopify.routes.root}cart.js`;
      try {
        return await fetch(url).then(response => response.json());
      } catch (error) {
        console.error(error);
      }
    };
  }

  /**
   * Clear cart
   */
  get clear() {
    return async () => {
      const url = `${Shopify.routes.root}cart/clear.js`;
      try {
        return await fetch(url, { method: 'POST' }).then(response => response.json());
      } catch (error) {
        console.error(error);
      }
    };
  }

  /**
   * Check if modal should be opened
   * We must not return undefined otherwise openModal will default to true
   */
  get openModal() {
    return btn =>
      this.settings?.quickBuy?.openModal ||
      (btn.closest('.shopify-section')?.id?.includes('_main') && location.pathname.includes('/products/'));
  }

  /* #endregion ══════════════════════════════════════════════════════════════════ */
}
/* #endregion ═══════════════════════════════════════════════════════════════════════════════ */

/* #region(collapsed) CART UI ═══════════════════════════════════════════════════════════════ */
class CartUI extends HTMLElement {
  async connectedCallback() {
    this.aborter = new AbortController();
    this.controller = MaxDesign.cart.controller;
    this.settings = this.controller.json(this);
    this.setVariables();
    this.updateCount();
    await customElements.whenDefined('cart-item-maxdesign');
    if (this.settings.clearCart) this.controller.clear();
    else if (this.settings.cart.item_count > 0) this.init();
    else if (this.controller.modal) this.controller.modal.loading = false;
    else this.loading = { el: this, load: false };
  }

  disconnectedCallback() {
    this.aborter.abort();
  }

  init() {
    this.onInit(); /* on cart init (no user interaction) */
    this.onSubmitCart(); /* button loader on cart submit */
    this.onInputDiscount(); /* update hidden discount input on keyup */
    this.onSubmitDiscount(); /* apply discount on cart page */
    this.onRemoveDiscount(); /* remove discount promotion on close action */
    this.onAddonIntersect(); /* toggle add-on visibility in sticky ATC */
    this.onATCBtnIntersect(); /* toggle fixed button */
    this.onSubmitGiftMessage(); /* add a personalized message card */
    this.onSubmitGiftWrapping(); /* wrap any cart item */
    this.onAddonToggle(); /* add or remove product add-on */
    this.onNoteChange(); /* update notes before cart update */
  }

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Set Variables */
  setVariables() {
    this.discount = { ...this.settings.discount };
    this.prices = {
      subtotal: this.settings.cart.original_total_price,
      total: this.settings.cart.total_price,
      discount: this.settings.cart.total_discount,
      shipping: this.settings.shipping.price.value,
    };
    this.DOM = {
      addon: {
        input: this.querySelector('#cmd-addon-toggle'),
        form: this.querySelector('form.cmd-addon'),
      },
      discount: {
        remove: this.querySelectorAll('.cmd-discount-preview__delete'),
        text: this.querySelector('.cmd-discount-field__text'),
        btn: this.querySelector('.cmd-discount-field__btn'),
        input: this.querySelector('.cmd-discount-field input'),
      },
      global: {
        bubbles: this.settings.global.bubble && document.querySelectorAll(this.settings.global.bubble),
        counters: this.settings.global.counter && document.querySelectorAll(this.settings.global.counter),
        prices: this.settings.global.prices && document.querySelectorAll(this.settings.global.prices),
      },
      note: this.querySelector('#cmd-note'),
      offer: {
        container: this.querySelector('.cmd-offer'),
        progress: this.querySelector('.cmd-offer__progress'),
        text: this.querySelector('.cmd-offer__text'),
      },
      price: {
        total: this.querySelectorAll('[data-price-cart-total]'),
        delivery: this.querySelector('.cmd-delivery__price'),
      },
      shipping: {
        estimate: this.querySelector('.cmd-shipping [data-shipping-estimate]'),
      },
      slider: {
        container: this.querySelector('.cmd-trust__slider'),
        btns: this.querySelectorAll('.cmd-trust__arrows button'),
      },
      summary: {
        details: this.querySelectorAll('.cmd-upsell__description summary'),
        btnATC: this.querySelector('.cmd-btn'),
        btnSticky: this.querySelector('.cmd-btn__sticky'),
        terms: this.querySelector('.cmd-terms'),
      },
      timer: {
        el: this.querySelector('.cmd-timer'),
        hrs: this.querySelector('#cmd-timer--hours'),
        mins: this.querySelector('#cmd-timer--minutes'),
        secs: this.querySelector('#cmd-timer--seconds'),
      },
    };
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) On Cart Init */
  async onInit() {
    await this.updateAddon(); // add or remove product addon
    await this.shouldCartSwap(); // swap cart variants when discount option
    this.updateOffers(); // update upsell on cart init
    this.updateMaxInventories(); // check if some items are over master inventory limit
    this.shippingEstimate(); // custom shipping estimate
    this.cdTimer('cart_timer'); // Countdown timer with cookie deposit
    this.trustSlider(); // trust slider with dot navigation
    this.iosFixes(); // ios fix when returning from checkout
    this.ellipsisFixes(); // remove ellipsis when not necessary
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) On User Interaction */

  /**
   * Redirect to cart or checkout + animate button
   * fetch('https://domain.name/cart.json').then((resp) => resp.json()).then(console.log)
   */
  onSubmitCart() {
    const { btnATC, btnSticky, terms } = this.DOM.summary;
    const CTAOverrideForce = this.settings.apps.checkout_cta_override;
    document.addEventListener(
      'click',
      e => {
        if (!(btnATC.contains(e.target) || btnSticky.contains(e.target))) return;
        if (this.controller.hasAttribute('data-cart-redirect')) {
          this.controller.loading = { el: btnATC, load: true };
          this.controller.loading = { el: this.DOM.summary.btnSticky, load: true };
          e.preventDefault();
          location.href = `${Shopify.routes.root}cart#cmd-ui`;
        } else if (!terms || terms.querySelector('input').checked) {
          if (!e.defaultPrevented) {
            this.controller.loading = { el: btnATC, load: true };
            this.controller.loading = { el: this.DOM.summary.btnSticky, load: true };
            if (this.controller.modal) this.controller.modal.overlay = { state: 'loading' };
          }
          if (CTAOverrideForce !== 'default') {
            e.preventDefault();
            CTAOverrideForce == 'force_2' && e.stopImmediatePropagation();
            location.href = `${Shopify.routes.root}checkout`;
          }
        }
      },
      { capture: CTAOverrideForce == 'force_2', signal: this.aborter.signal }
    );
  }

  async onSubmitGiftMessage() {
    const modals = document.querySelectorAll('.cmd-gift-note');
    if (!modals.length) return;
    await customElements.whenDefined('modal-maxdesign');
    // add or change gift message
    modals.forEach(modal => {
      const upsell = modal.settings.upsell;
      const textarea = modal.querySelector('textarea');
      const btn = modal.querySelector('button');
      btn.addEventListener(
        'click',
        () => {
          const [key, value] = [upsell.propKey, textarea.value];
          const data = { id: upsell.id, properties: value.trim() === '' ? { [`_${key}`]: ' ' } : { [key]: value } };
          if (modal.details?.giftNote) this.controller.editCart('change', { data, btn });
          else this.controller.editCart('add', { data: { items: [data] }, btn, scroll: false });
        },
        { signal: this.aborter.signal }
      );
      // restore item gift message
      document.addEventListener(
        'maxdesign:modal:open',
        ({ detail }) => detail === modal && (textarea.value = modal.details.giftNote || ''),
        { signal: this.aborter.signal }
      );
    });
  }

  async onSubmitGiftWrapping() {
    const modals = document.querySelectorAll('.cmd-gift-wrap');
    if (!modals.length) return;
    await customElements.whenDefined('modal-maxdesign');
    modals.forEach(modal => {
      const upsell = modal.settings.upsell;
      const controls = [...modal.querySelectorAll('.cmd-gift__qty-controls')];
      if (!controls.length && !upsell.reset) return;
      const weakMap = new WeakMap();
      const submitBtn = modal.querySelector('.cmd-gift__btn');
      const priceDisplay = submitBtn?.querySelector('[data-price]');
      const totalQty = () => controls.reduce((sum, control) => sum + weakMap.get(control).quantity, 0);
      const itemData = () => {
        const [id, quantity, properties] = [upsell.id, totalQty(), {}];
        const key = upsell.propKeys[quantity > 1 ? 'plural' : 'singular'];
        properties[key] = controls
          .map(control => {
            const data = weakMap.get(control);
            return data.quantity && `${data.title} (${data.quantity})`;
          })
          .filter(Boolean)
          .join(' ⁄ ');
        return { id, quantity, properties };
      };
      const updateUpsell = () => {
        upsell.action == 'change'
          ? this.controller.editCart('change', { data: itemData(), btn: submitBtn })
          : this.controller.editCart('add', { data: { items: [itemData()] }, btn: submitBtn, scroll: false });
      };
      controls.forEach(control => {
        const btns = control.querySelectorAll('button');
        const input = control.querySelector('input');
        const data = { title: input.dataset.title, quantity: parseInt(input.value, 10) };
        weakMap.set(control, data);
        btns.forEach(btn =>
          btn.addEventListener(
            'click',
            e => {
              const delta = +e.target.dataset.step;
              const qty = parseInt(input.value, 10) + delta;
              input.defaultValue = data.quantity = qty;
              btns[0].disabled = qty == 0;
              btns[1].disabled = qty == input.max;
              const qtyTotal = totalQty();
              submitBtn.dataset.hiddenPrice = qtyTotal == 0;
              submitBtn.dataset.content = qtyTotal == 0 ? upsell.button.unwrap : upsell.button.wrap;
              priceDisplay.textContent = this.formatMoney(upsell.price * qtyTotal);
              submitBtn.hidden = submitBtn.disabled = false;
              submitBtn.scrollIntoView();
            },
            { signal: this.aborter.signal }
          )
        );
      });
      // updateUpsell() on modal close event and when a wrapped item was deleted
      upsell.reset && updateUpsell();
      submitBtn?.addEventListener('click', modal.close.bind(modal), { signal: this.aborter.signal });
      document.addEventListener(
        'maxdesign:modal:close',
        ({ detail }) => detail === modal && !submitBtn.disabled && updateUpsell(),
        { signal: this.aborter.signal }
      );
    });
  }

  /**
   * Update hidden discount on input
   * Submit discount on enter key
   */
  onInputDiscount() {
    const { input, btn, text } = this.DOM.discount;
    if (!input) return;
    input.oninput = () => (input.value = input.value.toUpperCase());
    input.addEventListener(
      'keyup',
      e => {
        btn.disabled = !input.value;
        text.hidden = true;
        input.style.removeProperty('--color-discount-field');
        if (e.key === 'Enter') e.preventDefault(), btn.click();
      },
      { signal: this.aborter.signal }
    );
  }

  /**
   * Apply discount on cart page
   */
  onSubmitDiscount() {
    const { input, btn } = this.DOM.discount;
    if (!btn) return;
    btn.addEventListener(
      'click',
      () => {
        this.controller.editDiscounts({ add: input.value, btn });
      },
      { signal: this.aborter.signal }
    );
  }

  /**
   * Remove discount promotion on close action
   */
  onRemoveDiscount() {
    const remove = this.DOM.discount.remove;
    if (!remove) return;
    remove.forEach(btn =>
      btn.addEventListener(
        'click',
        () => {
          const codeToRemove = btn.parentElement.dataset.discount;
          this.controller.editDiscounts({ remove: codeToRemove, btn });
        },
        { signal: this.aborter.signal }
      )
    );
  }

  /**
   * We show addon in sticky form when the main addon is not visible
   */
  onAddonIntersect() {
    const stickyAddon = document.querySelector('.cmd-form__sticky .cmd-addon');
    if (!stickyAddon) return;
    const options = {
      threshold: 1,
      rootMargin: `0px 0px -${this.DOM.summary.btnSticky.offsetHeight + 20}px`,
      root: location.pathname.indexOf('/cart') > -1 ? null : this.controller.modal,
    };
    new IntersectionObserver(([{ isIntersecting }]) => {
      this.toggleAttribute('data-addon', !isIntersecting);
      setTimeout(() => (stickyAddon.style.transition = '.2s cubic-bezier(0.46, 0.03, 0.52, 0.96)'));
    }, options).observe(this.DOM.addon.form);
  }

  /**
   * Toggle fixed button
   * If there is an opt-out addon, we only show fixed button when main ATC has been seen at least once
   */
  onATCBtnIntersect() {
    const options = { threshold: 0.5, root: location.pathname.indexOf('/cart') > -1 ? null : this.controller.modal };
    new IntersectionObserver(([{ isIntersecting }]) => {
      this.toggleAttribute('data-sticky', !isIntersecting);
    }, options).observe(this.DOM.summary.btnATC);
  }

  /**
   * Update add-on product when toggle is switched by user
   */
  onAddonToggle() {
    this.DOM.addon.input?.addEventListener('change', e => {
      if (e.isTrusted) {
        if (e.target.checked) localStorage.removeItem('maxdesign:cart:addon_disabled');
        else localStorage.setItem('maxdesign:cart:addon_disabled', 'true');
        this.updateAddon();
      }
    });
  }

  onNoteChange() {
    let debounceTimeout;
    this.DOM.note?.addEventListener('input', () => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        this.controller.editCart('update', { data: { note: this.DOM.note.value }, render: false, load: false });
      }, 500);
    });
  }

  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Solve & Swap Cart for Bundle Qty Offers */

  /**
   * Check if we need to swapCart()
   * Combined with a product option "discount", this allows custom discount at the variant level
   * Disable loader asap depending on outcome
   */
  async shouldCartSwap() {
    if (this.controller.modal) this.controller.modal.loading = this.discount.qtyBreak;
    else this.loading = { el: this, load: this.discount.qtyBreak };
    if (!this.discount.qtyBreak) return;

    const offerItems = this.items.filter(item => item.settings.qtyBreaks.enable);
    if (offerItems.length) {
      // make group of items with same product/offer id
      const offerGroups = offerItems.reduce((obj, item) => {
        const offerId = item.settings.qtyBreaks.offerId;
        obj[offerId] = [...(obj[offerId] || []), item];
        return obj;
      }, {});
      // loop through each group, then make sets
      for (const [offerId, group] of Object.entries(offerGroups)) {
        const offerSet = group.reduce((obj, item) => {
          obj.qty = (obj.qty || 0) + item.settings.data.quantity;
          obj.discounts = (obj.discounts || []).concat([item.settings.qtyBreaks.currentValue]);
          return obj;
        }, {});
        // determine if swap cart is required
        const discountValues = group[0].settings.qtyBreaks.discountValues;
        const validDiscount = discountValues[Math.min(offerSet.qty - 1, discountValues.length - 1)];
        const needSwapCart = !offerSet.discounts.every(discount => discount == validDiscount);
        if (needSwapCart) {
          await this.swapCart(discountValues, validDiscount, offerId);
          return;
        }
      }
    }
    if (this.controller.modal) this.controller.modal.loading = false;
    else this.loading = { el: this, load: false };
  }

  /**
   * permute variants with their matching discounted option
   * 1 - loop through all cart items
   * 2 - loop through their variants
   * 3 - push matching variant to array (based on new discount option)
   * 4 - push other items like before
   * 4 - clear cart and rebuild it in order
   * @param {Array} discountValues - all discount values
   * @param {string} validDiscount - new discount value to apply
   * @param {string} offerId - offerId to know what product to target
   */
  async swapCart(discountValues, validDiscount, offerId) {
    // build array of variants to add
    const variantsToAdd = this.items.map(({ settings }) => {
      const { qtyBreaks, item } = settings;
      const matchingVariant =
        qtyBreaks.offerId === offerId &&
        qtyBreaks.variants.find(
          variant =>
            variant.options.includes(validDiscount) &&
            this.newVariantMatchItem(discountValues, variant.options, item.variant_options)
        );
      return { id: matchingVariant?.id || item.id, quantity: item.quantity, properties: item.properties };
    });
    // clear cart and rebuild it
    await this.controller.clear();
    await this.controller.editCart('add', { data: { items: variantsToAdd.reverse() } });
  }

  /**
   * determine whether variant is a match with main item options
   * @param {Array} discountValues - Discount values
   * @param {Array} variantOptions - Variant options
   * @param {Array} itemOptions - Current item variant options
   * @return {Boolean} - Whether new discounted variant match
   */
  newVariantMatchItem(discountValues, variantOptions, itemOptions) {
    const mainVariantOptions = variantOptions.filter(option => !discountValues.includes(option));
    const mainItemOptions = itemOptions.filter(option => !discountValues.includes(option));
    return mainVariantOptions.every((option, index) => option == mainItemOptions[index]);
  }

  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Master Variant Inventory */

  /**
   * Update max inventories of items with the same ID
   * This adjusts variants inventory based on one master "option"
   * While reliable, it's not 100% foolproof and must be used with MaxDesign's "Master Inventory" Shopify Flow action
   * /cart/change.js is invoked on its own with the observer method onInventoryMutation()
   */
  updateMaxInventories() {
    const items = this.items.filter(item => item.settings.masterInventory.enable);
    if (items.length < 2) return;
    // build sets by id
    const sets = items.reduce((obj, item) => {
      const { masterInventory, data } = item.settings;
      const id = masterInventory.id;
      obj[id] = obj[id]
        ? { ...obj[id], totalQty: obj[id].totalQty + data.quantity, items: [...obj[id].items, item] }
        : { ...masterInventory, totalQty: data.quantity, items: [item] };
      return obj;
    }, {});
    // update max value for each item in sets
    Object.values(sets).forEach(({ totalQty, inventoryQty, items }) => {
      const delta = totalQty - inventoryQty;
      if (delta == 0) items.forEach(item => (item.qtyInput.max = item.settings.data.quantity));
      else if (delta < 0) items.forEach(item => (item.qtyInput.max = inventoryQty));
      else {
        const itemToUpdate = items.sort((a, b) => b.settings.data.quantity - a.settings.data.quantity)[0];
        const qtyMax = inventoryQty - totalQty + itemToUpdate.settings.data.quantity;
        items.forEach(item => (item.qtyInput.max = item == itemToUpdate ? qtyMax : item.settings.data.quantity));
      }
    });
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Dynamic Cart Update */

  /**
   * update cart count textContent or data-attr for pseudo elements
   * update cart indications (bubbles, etc.)
   * update cart price (for themes that indicate total price)
   */
  updateCount() {
    const itemCount = this.settings.clearCart ? 0 : this.settings.cart.item_count;
    this.DOM.global.counters &&
      this.DOM.global.counters.forEach(el => {
        const textContent = el.textContent.trim();
        if (textContent && !isNaN(textContent)) el.textContent = itemCount;
        for (const [key, value] of Object.entries(el.dataset)) if (!isNaN(value)) el.dataset[key] = itemCount;
      });
    this.DOM.global.bubbles &&
      this.DOM.global.bubbles.forEach(el => {
        this.settings.global.bubbleClassOn && el.classList.toggle(this.settings.global.bubbleClassOn, itemCount > 0);
        this.settings.global.bubbleClassOff && el.classList.toggle(this.settings.global.bubbleClassOff, itemCount == 0);
      });
    this.DOM.global.prices &&
      this.DOM.global.prices.forEach(el => {
        el.textContent = this.formatMoney(this.settings.cart.total_price);
      });
  }

  /**
   * We add or remove add-on based on opt-out setting and user local preference
   */
  async updateAddon() {
    const { input, form } = this.DOM.addon;
    if (!input) return;
    const userMustOptout = this.settings.addon.optout;
    const userDisabledAddon = localStorage.getItem('maxdesign:cart:addon_disabled');
    const variantId = form.id.value;
    const itemAdded = this.settings.cart.items.find(({ variant_id }) => variant_id == variantId);
    if (itemAdded && !input.checked) {
      const data = { id: variantId, quantity: 0 };
      await this.controller.editCart('change', { data, scroll: false, load: false });
    } else if (!itemAdded) {
      if (userMustOptout && !userDisabledAddon) input.checked = true;
      if (input.checked) {
        await this.controller.editCart('add', { form, scroll: false, load: false });
      }
    }
  }

  /**
   * update total price
   */
  updateTotal() {
    this.prices.total = this.prices.subtotal - this.prices.discount + this.prices.shipping;
    this.DOM.price.total.forEach(el => (el.innerHTML = this.formatMoney(this.prices.total)));
  }

  /**
   * Check for each step if current offer exceeds threshold
   * For shipping => threshold value must take into account discount value
   * For product => threshold value must take into account product price
   * > if step 4 qualify, congrats text
   * > if step 3 qualify, promote step 4
   * > if step 2 qualify, promote step 3
   * > if step 1 qualify, promote step 2
   * > if none qualify, promote step 1
   */
  async updateOffers() {
    const { offers } = this.settings;
    if (!offers.enable) return;

    const steps = offers.steps;
    let cartThreshold = offers.system === 'price' ? this.prices.total : this.settings.cart.item_count;
    let breakIndex = -1;

    const updateText = step => {
      const lastStepAchieved = steps[steps.length - 1].achieved;
      const goal = step[step.type].threshold - cartThreshold;
      const threshold = step.thresholdValue;
      const text = lastStepAchieved ? offers.qualifyText : step.text;

      this.DOM.offer.container.dataset.step = steps.filter(step => step.achieved).length;
      this.DOM.offer.text.innerHTML = text
        .replace('[goal-to-threshold]', offers.system === 'price' ? this.formatMoney(goal) : goal)
        .replace('[threshold]', offers.system === 'price' ? this.formatMoney(threshold) : threshold);
    };

    const updateDelivery = step => {
      if (!this.settings.shipping.enable) return (this.prices.shipping = 0);

      const defaultPrice = this.settings.shipping.price.text.replace(/[,.]/g, '');
      const newPrice = step.shipping.price.replace(/[,.]/g, '');
      const priceFromText = step.achieved ? newPrice : defaultPrice;
      const formattedPrice = isNaN(+priceFromText) ? priceFromText : this.formatMoney(+priceFromText);

      this.DOM.price.delivery.innerHTML =
        step.achieved && !isNaN(+defaultPrice)
          ? `<s>${this.formatMoney(+defaultPrice)}</s> ${formattedPrice}`
          : formattedPrice;

      this.prices.shipping = isNaN(+priceFromText) ? 0 : +priceFromText;
      this.updateTotal();
    };

    const updateProduct = async step => {
      const data = step.product;
      const dataIds = [data.id, ...data.upsell.ids].filter(Boolean);

      const addProduct = data.id && step.achieved && !data.added;
      const removeProducts = dataIds.length && !step.achieved && data.added && offers.denyProductAction === 'remove';
      const adjustProducts = dataIds.length && step.achieved && data.added && offers.denyProductAction === 'remove';

      if (addProduct) {
        await this.controller.editCart('add', { data: { items: [{ id: data.id, properties: data.prop }] } });
      } else if (removeProducts) {
        const updates = data.items
          .filter(item => dataIds.includes(item.id))
          .reduce((acc, item) => ({ ...acc, [item.key]: 0 }), {});
        if (Object.keys(updates).length) await this.controller.editCart('update', { data: { updates } });
      } else if (adjustProducts) {
        const updates = data.items
          .filter(item => item.final_price == 0 && item.quantity > 1)
          .reduce((acc, item) => ({ ...acc, [item.key]: 1 }), {});
        if (Object.keys(updates).length) await this.controller.editCart('update', { data: { updates } });
      }
    };

    const updateBar = step => {
      const progress = this.DOM.offer.progress;
      const thresholdValue = steps[steps.length - 1].thresholdValue;
      const value =
        offers.system === 'price'
          ? this.formatMoney(cartThreshold).replace(/<\/?[^>]+(>|$)/g, '')
          : `×${cartThreshold}`;
      const percentage = Math.min((cartThreshold * 100) / thresholdValue, 100);
      progress.textContent = value;
      progress.style.setProperty('--bar-progress', `${Math.min(percentage - 2, 100)}%`);
    };

    const processStep = async (step, index, isCleanup = false) => {
      const { type, thresholdValue, product } = step;

      switch (type) {
        case 'shipping':
          step[type].threshold = thresholdValue;
          step.achieved = cartThreshold >= step[type].threshold;
          if (!isCleanup) updateDelivery(step);
          break;

        case 'product':
          // const cartThresholdDeduction = offers.system === 'price' ? this.prices.discount : 0;
          cartThreshold = this.items.reduce((total, { settings }) => {
            const amount = settings.data[offers.system === 'price' ? 'line_price' : 'quantity'];
            return total + (settings.offer.enable ? amount : 0);
          }, 0);
          step[type].items = this.settings.cart.items.filter(
            ({ variant_id }) => product.id == variant_id || product.upsell.ids.includes(variant_id)
          );
          step[type].added = step[type].items?.length;
          step[type].threshold = thresholdValue;
          step.achieved = cartThreshold >= step[type].threshold;
          await updateProduct(step);
          break;

        default:
          step[type] = step[type] || {};
          step[type].threshold = thresholdValue;
          step.achieved = cartThreshold >= thresholdValue;
          break;
      }

      if (!isCleanup && (!step.achieved || index === steps.length - 1)) {
        updateText(step);
        updateBar(step);
        breakIndex = index;
        return true;
      }
    };

    // Evaluate steps until breakIndex
    for (let i = 0; i < steps.length; i++) {
      const shouldBreak = await processStep(steps[i], i);
      if (shouldBreak) break;
    }

    // Cleanup pass: remove lingering product offers
    for (let i = breakIndex + 1; i < steps.length; i++) {
      const step = steps[i];
      if (step.type === 'product') await processStep(step, i, true);
    }
  }

  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Utility Methods */

  /**
   * money formater.
   */
  formatMoney(cents) {
    const { moneyFormat } = this.settings;
    const regex = /\{\{\s*(\w+)\s*\}\}/;
    const placeholder = moneyFormat.match(regex)[1];
    const [precision, thousands, decimal] = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
    }[placeholder];
    const formatWithDelimiters = number => {
      if (isNaN(number) || number === null) return 0;
      const fixedNumber = (number / 100.0).toFixed(precision);
      const [dollars, fraction] = fixedNumber.split('.');
      const thousandsRegex = /(\d)(?=(\d{3})+$)/g;
      return (
        dollars.replace(thousandsRegex, `$1${thousands}`) + (fraction && +fraction !== 0 ? decimal + fraction : '')
      );
    };
    const value = formatWithDelimiters(typeof cents === 'string' ? cents.replace('.', '') : cents);
    return moneyFormat.replace(regex, value);
  }

  /**
   * ios fix when returning from checkout
   */
  iosFixes() {
    addEventListener(
      'pageshow',
      event => {
        if (event.persisted) {
          this.controller.loading = { el: this.DOM.summary.btnATC, load: false };
          this.controller.loading = { el: this.DOM.summary.btnSticky, load: false };
          if (this.controller.modal?.frame?.getAttribute('data-modal') === 'loading')
            this.controller.modal.overlay = { state: 'visible' };
        }
      },
      { signal: this.aborter.signal }
    );
  }

  /**
   * Remove ellipsis when not necessary
   */
  ellipsisFixes() {
    this.DOM.summary.details.forEach(summary => {
      if (summary.scrollWidth <= summary.clientWidth) summary.style.setProperty('--display', 'none');
    });
  }

  /**
   * Estimate shipping time in custom block
   */
  shippingEstimate() {
    if (!this.settings.shipping.estimate.enable) return;
    const { estimate } = this.DOM.shipping;
    if (!estimate) return;
    const { delayMin, delayMax, weekday, skipSat, skipSun, skipHol, language, country } =
      this.settings.shipping.estimate;
    const date = new Date();
    const year = date.getFullYear();
    const locales = `${language}-${country}`;

    const addDays = (date, days) => {
      const newDate = new Date(date);
      while (days > 0) {
        newDate.setDate(newDate.getDate() + 1);
        const dayIndex = newDate.getDay();
        if ((skipSun && dayIndex === 0) || (skipSat && dayIndex === 6)) continue;
        days--;
      }
      return newDate;
    };

    const getDay = (date, holidays) => {
      const isHoliday = holidays?.some(item => item.date == date.toISOString().slice(0, 10));
      return isHoliday ? addDays(date, 1) : date;
    };

    const fetchHolidays = async () => {
      const response = await Promise.race([
        fetch(`https://date.nager.at/api/v3/publicholidays/${year}/${country}`),
        new Promise((_, reject) => setTimeout(() => reject(new Error('date.nager api fetch request timed out')), 300)),
      ]);
      const holidays = await response.json();
      if (holidays.errors) throw holidays.errors;
      return holidays;
    };

    const getDeliveryDate = async () => {
      let start, end;
      try {
        const holidays = skipHol ? await fetchHolidays() : null;
        start = getDay(addDays(date, delayMin), holidays);
        end = getDay(addDays(date, delayMax), holidays);
      } catch (error) {
        console.error(error);
        start = getDay(addDays(date, delayMin));
        end = getDay(addDays(date, delayMax));
      }
      const formatOptions = {
        weekday: weekday ? 'short' : undefined,
        day: '2-digit',
        month: start.getMonth() != end.getMonth() || weekday ? 'short' : undefined,
      };
      const startFormat = start.toLocaleDateString(locales, formatOptions).replace(/ /g, '\u00A0');
      const endFormat = end.toLocaleDateString(locales, { ...formatOptions, month: 'short' }).replace(/ /g, '\u00A0');
      return start.getDate() != end.getDate() ? `${startFormat}&nbsp;-&nbsp;${endFormat}` : `${endFormat}`;
    };

    getDeliveryDate().then(result => (estimate.innerHTML = result));
  }

  /**
   * run cdTimer with cookies
   */
  cdTimer(name) {
    if (!this.settings.cdTimer.enable) return;
    const cookieDeadline = this.cookie(name);
    const initialDeadline = Date.now() + this.settings.cdTimer.time * 60000;
    const cookieExpire = new Date(initialDeadline + this.settings.cdTimer.expiry * 60000).toUTCString();
    const deadline = cookieDeadline || initialDeadline;

    // write cookie
    if (!cookieDeadline) this.cookie = { key: name, value: deadline, expires: cookieExpire };

    // init timer
    const updateClock = () => {
      if (this.aborter.signal.aborted) return;
      const total = deadline - Date.now();
      const time = {
        hrs: Math.floor((total / (1000 * 60 * 60)) % 24),
        mins: Math.floor((total / 1000 / 60) % 60),
        secs: Math.floor((total / 1000) % 60),
      };
      if (total > 0 && !isNaN(total)) {
        this.DOM.timer.hrs.innerHTML = time.hrs == 0 ? '' : `${('0' + time.hrs).slice(-2)}&thinsp;:&thinsp;`;
        this.DOM.timer.mins.innerHTML = `${('0' + time.mins).slice(-2)}&thinsp;:&thinsp;`;
        this.DOM.timer.secs.innerHTML = ('0' + time.secs).slice(-2);
        setTimeout(updateClock.bind(this), 1000);
        this.setAttribute('data-timer', '');
      } else this.DOM.timer.el?.remove();
    };
    updateClock();
  }

  /**
   * Trust badges slider with arrow navigation
   * Code logic borrowed from logo bar module
   * We don't need it in mobile (except theme editor)
   */
  trustSlider() {
    const slider = this.DOM.slider.container;
    if (!slider || (this.isMobile && !Shopify.designMode)) return;
    const btns = this.DOM.slider.btns;
    const slides = [...slider.children];
    const slidesInView = new Set();
    const gapWidth = 25;
    let selectedIndex = 0;
    let hasProgrammaticScroll, groups, scrollTimeout, _scrollWidth, _offsetWidth;

    const scrollLeft = () => Math.round(slider.scrollLeft);
    const scrollWidth = () => _scrollWidth || (_scrollWidth = slider.scrollWidth);
    const offsetWidth = () => _offsetWidth || (_offsetWidth = slider.offsetWidth);
    const scrollIndex = () => {
      const scrollLeftVal = scrollLeft();
      const isMinScroll = scrollLeftVal === 0;
      const isMaxScroll = scrollLeftVal === scrollWidth() - offsetWidth();
      const slideInView = slidesInView.values().next().value;
      if (isMinScroll) return +slides[0].dataset.group;
      if (isMaxScroll) return +slides[slides.length - 1].dataset.group;
      return slideInView ? +slideInView.dataset.group : selectedIndex;
    };
    const groupSlides = () => {
      const initialValues = { index: 0, width: 0, sets: {} };
      groups = slides.reduce((group, slide) => {
        const slideWidth = slide.offsetWidth;
        const slideTotalWidth = slideWidth + gapWidth;
        if (group.width + slideTotalWidth > offsetWidth()) {
          group.index++;
          group.width = 0;
        }
        group.width += slideTotalWidth;
        slide.dataset.group = group.index;
        const set = group.sets[group.index] || (group.sets[group.index] = { slides: [], totalWidth: 0 });
        set.slides.push(slide);
        set.totalWidth += slideWidth + (set.slides.length > 1 ? gapWidth : 0);
        return group;
      }, initialValues);
      slider.dataset.groups = groups.index;
    };
    const observeSlides = () => {
      const options = { root: slider, threshold: 1 };
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) slidesInView.add(entry.target);
          else slidesInView.delete(entry.target);
        });
      }, options);
      slides.forEach(slide => observer.observe(slide));
    };
    const watchControls = () => {
      btns.forEach(btn => {
        btn.addEventListener('click', () => {
          const [minIndex, maxIndex] = [0, groups.index];
          selectedIndex = Math.max(minIndex, Math.min(maxIndex, selectedIndex + +btn.dataset.step));
          slider.dispatchEvent(new CustomEvent('controls:change', { detail: { index: selectedIndex } }));
        });
      });
    };
    const progressiveScrollEnd = () => {
      clearTimeout(scrollTimeout);
      if (window.onscrollend === void 0)
        scrollTimeout = setTimeout(() => slider.dispatchEvent(new CustomEvent('scrollend')), 100);
    };
    const onControlsChange = e => selectSlide(e.detail.index);
    const onSliderScroll = () => {
      progressiveScrollEnd();
      if (!hasProgrammaticScroll) selectedIndex = scrollIndex();
    };
    const onSliderScrollEnd = () => {
      updateArrows();
      hasProgrammaticScroll = false;
    };
    const onResizeObserve = () => {
      _scrollWidth = _offsetWidth = null;
      groupSlides();
      updateArrows();
    };
    const selectSlide = index => {
      hasProgrammaticScroll = true;
      const group = groups.sets[index];
      const frameWidth = this.controller?.modal?.offsetWidth || innerWidth;
      const scrollLeftVal = group.slides[0].offsetLeft + (group.totalWidth - frameWidth) / 2;
      slider.scrollTo({ left: scrollLeftVal, behavior: 'smooth' });
    };
    const updateArrows = () => {
      const scrollLeftVal = scrollLeft();
      const isMinScroll = scrollLeftVal === 0;
      const isMaxScroll = scrollLeftVal === scrollWidth() - offsetWidth();
      btns[0].disabled = isMinScroll;
      btns[1].disabled = isMaxScroll;
    };

    observeSlides();
    watchControls();
    slider.addEventListener('scroll', onSliderScroll);
    slider.addEventListener('scrollend', onSliderScrollEnd);
    slider.addEventListener('controls:change', onControlsChange);
    new ResizeObserver(onResizeObserve).observe(slider);
  }

  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Getters & Setters */

  /**
   * get cookie value by name
   * @param {string} name - name of cookie (key)
   */
  get cookie() {
    return name => {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? match[2] : null;
    };
  }

  /**
   * @returns {Boolean} mobile device
   */
  get isMobile() {
    return matchMedia('(max-width:480px)').matches;
  }

  /**
   * Set cookie
   * @param {string} key
   * @param {string} value
   * @param {string} expires - default to remove
   */
  set cookie({ key, value, expires = 'Thu, 01 Jan 1970 00:00:00 UTC' }) {
    return (document.cookie = `${key}=${value}; expires=${expires}; path=/`);
  }

  /* #endregion ═════════════════════════════════════════════════════════════ */
}
/* #endregion ═══════════════════════════════════════════════════════════════════════════════ */

/* #region(collapsed) CART ITEMS ════════════════════════════════════════════════════════════ */
class CartItem extends HTMLElement {
  connectedCallback() {
    this.aborter = new AbortController();
    this.cart = MaxDesign.cart.UI;
    this.controller = this.cart.controller;
    this.cart.items = this.cart.items || [];
    this.cart.items.push(this);
    this.settings = this.controller.json(this);
    this.qtyInput = this.querySelector('.cmd-item__qty input');
    this.qtyBtns = this.querySelectorAll('.cmd-item__qty button');
    this.deleters = this.querySelectorAll('.cmd-item__delete');
    this.groupEditer = this.querySelector('.cmd-item__qty-edit[data-edit-group]');
    this.masterInventoryOption = this.settings.masterInventory.option;
    this.addEventListener('change', this.onChange.bind(this));
    this.onInventoryMutation();
    this.onQtyUpdate();
    this.onPlanUpgrade();
    this.onProductGroupEdit();
  }

  disconnectedCallback() {
    this.aborter.abort();
    this.cart.updateCount();
  }

  /**
   * We update item quantity or selling_plan on select/input change event
   * @param {Object} e - change event
   */
  onChange(e) {
    const target = e.target;
    if (target.name == 'updates[]') {
      const qty = target.value;
      const qtyMax = Math.max(0, +this.qtyInput.max);
      this.qtyInput.defaultValue = Math.min(qty, qtyMax || qty);
      this.masterInventoryOption && this.cart.updateMaxInventories();
      this.preventIncrement = qtyMax && qty >= qtyMax;
    } else if (target.closest('.cmd-props__item--plan')) {
      const data = { line: this.dataset.itemLine, quantity: this.settings.data.quantity, selling_plan: target.value };
      this.controller.editCart('change', { data });
    }
  }

  /**
   * Update cart quantity on quantity click
   * 1 > Mobile selector
   * 2 > Increment / decrement buttons
   * 3 > Bin / cross
   */
  onQtyUpdate() {
    this.qtyBtns.forEach(button => {
      button.addEventListener(
        'click',
        e => {
          if (this.preventClick) return;
          const target = e.target;
          const delta = +target.dataset.step;
          const qty = parseInt(this.qtyInput.value, 10) + delta;
          const qtyMax = Math.max(0, +this.qtyInput.max);
          this.qtyInput.defaultValue = Math.min(qty, qtyMax || qty);
          this.masterInventoryOption && this.cart.updateMaxInventories();
          this.preventIncrement = qtyMax && qty >= qtyMax;
        },
        { signal: this.aborter.signal }
      );
    });
    this.deleters.forEach(el => {
      el.addEventListener(
        'click',
        e => {
          e.preventDefault();
          e.stopImmediatePropagation();
          let keys = this.settings.productGroup.keys;
          const updates = keys.split(',').reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
          this.controller.editCart('update', { data: { updates } });
        },
        { signal: this.aborter.signal }
      );
    });
  }

  /**
   * Observe input value change
   * Update cart item quantity in case max limit is exceeded
   * Modifying max value may decrease the value and invoke the observer once more
   */
  onInventoryMutation() {
    new MutationObserver(mutations => {
      mutations.forEach(({ attributeName, target }) => {
        const [currValue, maxValue] = [+target.value, +target.max];
        if (attributeName == 'value') {
          this.controller.editCart('change', { data: { id: this.settings.data.key, quantity: currValue } });
        } else if (attributeName == 'max') {
          this.preventIncrement = currValue >= maxValue;
          if (currValue > maxValue) target.defaultValue = maxValue;
        }
      });
    }).observe(this.qtyInput, { attributeFilter: ['value', 'max'] });
  }

  /**
   * We make sure current group is selected on select change
   * We submit new selling plan if select is valid
   */
  onPlanUpgrade() {
    const modal = document.querySelector(`.cmd-upgrade[data-id="selling-plan-upgrade-${this.settings.data.key}"]`);
    if (!modal) return;

    const btn = modal.querySelector('button[data-submit]');

    modal.addEventListener('change', e => e.target.closest('label')?.click());
    btn.addEventListener('click', () => {
      const selector = modal.querySelector('input:checked ~ select');
      if (selector?.reportValidity()) {
        const data = {
          line: this.dataset.itemLine,
          quantity: this.settings.data.quantity,
          selling_plan: selector.value,
        };
        this.controller.editCart('change', { data, btn });
      }
    });
  }

  onProductGroupEdit() {
    const link = this.groupEditer;
    link?.addEventListener('click', e => {
      e.preventDefault();
      const id = this.settings.productGroup.id;
      const variants = this.cart.items
        .filter(item => item.settings.productGroup.id == id && item.settings.data.properties['_option'])
        .map(item => item.settings.data);
      const url = new URL(link.href);
      url.searchParams.append('group', id);
      localStorage.setItem('productGroup', JSON.stringify(variants));
      location.href = url.href;
    });
  }

  /**
   * Prevent too many clicks on quantity buttons
   * @returns {boolean} - Returns true if the click is prevented, false otherwise
   */
  get preventClick() {
    const now = performance.now();
    if (now - this.lastClick < 400) {
      console.warn('Too many simultaneous requests');
      return true;
    }
    this.lastClick = now;
    return false;
  }

  /**
   * Prevents to increment quantities when out of stock
   * @param {Boolean} prevent
   */
  set preventIncrement(prevent) {
    this.qtyInput.dataset.increment = prevent ? 'deny' : 'allow';
    this.qtyBtns[1].disabled = prevent;
  }
}
/* #endregion ═══════════════════════════════════════════════════════════════════════════════ */

/* #region(collapsed) CART RECOMMENDATIONS ══════════════════════════════════════════════════ */
class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.controller = MaxDesign.cart.controller;
    this.cache = MaxDesign.cart.recommendationsCache;
    this.settings = this.controller.json(this);
    this.placeholder = '[data-recommendations-result]';
    this.result = this.querySelector(this.placeholder);
    Object.assign(this, this.settings);
    if (this.productId) this.loadRecommendations();
    else this.recommendations = false;
  }

  disconnectedCallback() {
    MaxDesign?.cart?.UI?.querySelector('.cmd-empty') && this.controller.modal?.removeAttribute('data-recommendations');
  }

  async loadRecommendations() {
    try {
      // check if the recommendations for the productId is already in cache
      const cached = this.cache[this.productId];
      if (cached) return this.renderRecommendations(cached);
      // fetch array of complementary and related products
      const url = `${Shopify.routes.root}recommendations/products?product_id=${this.productId}&limit=10&section_id=${this.controller.sectionId}`;
      const [resp1, resp2] = await Promise.all([fetch(`${url}&intent=complementary`), fetch(`${url}&intent=related`)]);
      const recom1 = new DOMParser().parseFromString(await resp1.text(), 'text/html').querySelector(this.placeholder);
      const recom2 = new DOMParser().parseFromString(await resp2.text(), 'text/html').querySelector(this.placeholder);
      recom1.append(...recom2.childNodes);
      // deduplicate & slice to limit before render
      if (recom1.childElementCount) {
        const ids = new Set();
        [...recom1.children].forEach((child, index) => {
          const id = child.getAttribute('id');
          if (ids.has(id) || index >= this.limit) child.remove();
          else ids.add(id);
        });
        this.renderRecommendations(recom1);
      } else this.recommendations = false;
    } catch (error) {
      console.error(error);
      this.recommendations = false;
    }
  }

  renderRecommendations(recom) {
    // cache result
    const preventCache = recom.querySelector('.cmd-upsell__subtitle [data-prevent-cache]');
    if (!preventCache) this.cache[this.productId] = recom;
    // append result && show recommendations
    this.result.replaceWith(recom);
    const totalRecom = recom.childElementCount + recom.parentElement.childElementCount - 2;
    const columns = getComputedStyle(this).getPropertyValue('--recommendations-columns') || 3;
    this.style.setProperty('--rows', Math.max(1, Math.floor(totalRecom / columns)));
    this.recommendations = true;
  }

  set recommendations(open) {
    if (!open) this.remove();
    this.toggleAttribute('data-performed', open);
    this.controller.modal?.toggleAttribute('data-recommendations', open);
  }
}
/* #endregion ═══════════════════════════════════════════════════════════════════════════════ */

/* #region(collapsed) REGISTER ELEMENTS ═════════════════════════════════════════════════════ */
customElements.define('cart-controller-maxdesign', CartController);
customElements.define('cart-ui-maxdesign', CartUI);
customElements.define('cart-item-maxdesign', CartItem);
customElements.define('cart-recommendations-maxdesign', CartRecommendations);
/* #endregion ═══════════════════════════════════════════════════════════════════════════════ */

/* #region(collapsed) COMMENTED CODE ════════════════════════════════════════════════════════ */

// fetch('/cart/update.js', {
//   method: 'POST',
//   headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
//   body: JSON.stringify({ attributes: { example_attribute: 'example_value' } }),
// })
//   .then(response => response.json())
//   .then(console.log)
//   .catch(console.error);

// const shopify_features_script = document.querySelector("script[id='shopify-features']");
// const shopify_features_json = JSON.parse(shopify_features_script.innerHTML);
// const cart = MaxDesign.cart.UI.settings.cart;
// const lineItems = cart.items;
// console.log('🚀 ~ MaxDesign.cart.UI.settings:', MaxDesign.cart.UI.settings);
// const headers = {
//   Authorization: 'Basic ' + btoa(shopify_features_json.accessToken),
//   Accept: '*  /*',
//   'Content-Type': 'application/json',
// };
// const body = {
//   checkout: {
//     line_items: lineItems,
//     discount_code: discounts.join(','),
//     country: Shopify.country,
//     presentment_currency: cart.currency,
//   },
// };
// const wallet = await fetch('/wallets/checkouts/', {
//   method: 'POST',
//   headers: headers,
//   body: JSON.stringify(body),
//   referrerPolicy: 'no-referrer',
// });
// const response = await wallet.json();
// console.log('🚀 ~ response:', response);
// if (response) {
//   await fetch(`${route}checkout?discount=${discounts.join(',') || 'clear'}`).then(async e => {
//     console.log('/checkout called', e);
//     this.controller.render(await this.fetch());
//     const UI = MaxDesign.cart.UI;
//     const cart = await (await fetch(route + 'cart.js')).json();
//     const active = new Set(cart.items.flatMap(item => item.discounts.map(({ title }) => title.toLowerCase())));
//     const codesNotAdded = add.split(',').filter(code => !active.has(code.toLowerCase()) && code.trim() !== '');
//     if (codesNotAdded.length > 0) {
//       const codePlaceholder = UI.DOM.discount.text.querySelector('span[data-code]');
//       codePlaceholder && (codePlaceholder.dataset.code = codesNotAdded.join(', '));
//       UI.DOM.discount.text.hidden = false;
//       UI.DOM.discount.input.style.setProperty('--color-discount-field', 'var(--color-critical-error)');
//     }
//   });
// }

/* #endregion ════════════════════════════════════════════════════════════════════════════════ */
